import Swal from 'sweetalert2'
import { API_URL } from './api'
import {
  clearHostCsrfToken,
  clearHostSessionKey,
  getHostSessionKey,
} from './hostScopedStorage'
import { isPublicMarketingRoute } from './publicMarketingRoutes'
import store from '../store'

const VERSION_URL = '/version.json'
const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

const STORAGE_DEPLOYED_KEY = 'frontend_deployed_version'
const STORAGE_LOADED_KEY = 'frontend_loaded_version'

let intervalId = null
let sessionKickInProgress = false

async function fetchCurrentVersion() {
  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store'
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.version || null
  } catch (e) {
    return null
  }
}

async function forceSessionLogout(message) {
  if (sessionKickInProgress) {
    return
  }
  sessionKickInProgress = true

  clearHostSessionKey()
  clearHostCsrfToken()
  store.commit('CLEAR_USER')

  await Swal.fire({
    title: 'Signed out',
    text: message,
    icon: 'warning',
    confirmButtonText: 'OK',
    allowOutsideClick: false,
    allowEscapeKey: false
  })

  window.location.href = '/login'
}

async function checkSessionActive() {
  const sessionKey = getHostSessionKey()
  if (!sessionKey || sessionKickInProgress) {
    return
  }

  try {
    const headers = { 'X-Session-Key': sessionKey }
    const response = await fetch(`${API_URL}/auth/user/`, {
      method: 'GET',
      headers,
      credentials: 'include',
      cache: 'no-store'
    })

    if (response.status === 401) {
      // Stale host session after logout / PayFast return on public signup must
      // not show "Signed out" and bounce to /login mid-workspace setup.
      const path = (window.location.pathname || '').replace(/\/+$/, '') || '/'
      if (isPublicMarketingRoute(path) || path.startsWith('/start')) {
        clearHostSessionKey()
        clearHostCsrfToken()
        store.commit('CLEAR_USER')
        return
      }
      await forceSessionLogout(
        'Your account has been deactivated or your session has ended.'
      )
    }
  } catch (e) {
    // Network errors should not sign the user out
  }
}

async function checkForUpdate(showImmediately = false) {
  await checkSessionActive()

  const currentVersion = await fetchCurrentVersion()

  if (!currentVersion) {
    return
  }

  const loadedVersion = localStorage.getItem(STORAGE_LOADED_KEY)

  // First run on this tab: initialise both loaded and deployed versions
  if (!loadedVersion) {
    localStorage.setItem(STORAGE_LOADED_KEY, currentVersion)
    localStorage.setItem(STORAGE_DEPLOYED_KEY, currentVersion)
    return
  }

  const deployedVersion = localStorage.getItem(STORAGE_DEPLOYED_KEY)

  if (deployedVersion !== currentVersion) {
    localStorage.setItem(STORAGE_DEPLOYED_KEY, currentVersion)
  }

  // Compare loaded vs currently deployed
  if (loadedVersion !== currentVersion || showImmediately) {
    await Swal.fire({
      title: 'New version available',
      text: 'A new version of this page is available. Refresh now to get the latest updates. If you have unsaved work, please save it before refreshing.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Refresh now',
      cancelButtonText: 'Later',
      allowOutsideClick: false,
      allowEscapeKey: true
    }).then((result) => {
      if (result.isConfirmed) {
        // After user confirms, reload the page to pick up the new bundle
        localStorage.setItem(STORAGE_LOADED_KEY, currentVersion)
        localStorage.setItem(STORAGE_DEPLOYED_KEY, currentVersion)
        window.location.reload()
      }
    })
  }
}

export async function startVersionPolling() {
  // Avoid multiple intervals if called more than once
  if (intervalId !== null) {
    return
  }

  // On app mount: set loaded (and deployed) version to current so we're in sync
  const currentVersion = await fetchCurrentVersion()
  if (currentVersion) {
    localStorage.setItem(STORAGE_LOADED_KEY, currentVersion)
    localStorage.setItem(STORAGE_DEPLOYED_KEY, currentVersion)
  }

  await checkSessionActive()

  intervalId = window.setInterval(() => {
    checkForUpdate(false)
  }, CHECK_INTERVAL_MS)
}
